// SPDX-License-Identifier: FSL-1.1-Apache-2.0
/**
 * @module RealtimeBadge
 * @package @geenius/analytics/react
 * @description Renders the React realtime-visitor badge used by dashboards to
 * surface current activity at a glance. The badge handles loading, empty, and
 * active states without requiring consumers to branch around it.
 */

import type { RealtimeStats } from "@geenius/analytics";
import { Badge } from "@geenius/ui/react";
import type React from "react";
import { useAnalyticsCopy } from "../lib/analyticsCopy";
import { cn } from "../lib/cn";

/**
 * Props for rendering the React realtime activity badge.
 *
 * @property stats Latest realtime stats snapshot, or `null` when unavailable.
 * @property isLoading Whether the realtime fetch is still pending.
 * @property className Optional class name merged into the badge wrapper.
 */
export interface RealtimeBadgeProps {
  stats: RealtimeStats | null;
  isLoading: boolean;
  className?: string;
}

/**
 * Renders a React badge showing the current active-visitor count.
 *
 * @param props Realtime stats and loading state for the badge.
 * @returns A badge element representing loading, empty, or active realtime state.
 */
export function RealtimeBadge(props: RealtimeBadgeProps): React.ReactElement {
  const copy = useAnalyticsCopy();

  if (props.isLoading) {
    const loadingLabel = `${copy.t("analytics.dashboard.realtime")} ${copy.t(
      "analytics.topPages.loading",
    )}`;

    return (
      <Badge
        aria-busy="true"
        aria-label={loadingLabel}
        className={cn("gap-2", props.className)}
        role="status"
        variant="secondary"
      >
        <span
          aria-hidden="true"
          data-analytics="realtime-badge"
          data-loading="true"
        >
          ⏳
        </span>
      </Badge>
    );
  }

  if (!props.stats) {
    const emptyLabel = copy.t("analytics.realtime.empty");

    return (
      <Badge
        aria-label={emptyLabel}
        className={cn("gap-2", props.className)}
        role="status"
        variant="secondary"
      >
        <span
          aria-hidden="true"
          data-analytics="realtime-badge"
          data-empty="true"
        >
          —
        </span>
      </Badge>
    );
  }

  const activeLabel = `${props.stats.activeVisitors} ${copy.t(
    "analytics.realtime.activeNow",
  )}`;

  return (
    <Badge
      aria-label={activeLabel}
      className={cn("gap-2", props.className)}
      role="status"
      variant={props.stats.activeVisitors > 0 ? "success" : "secondary"}
    >
      <span aria-hidden="true" data-analytics="realtime-dot">
        🟢
      </span>
      <span data-analytics="realtime-count">{props.stats.activeVisitors}</span>
      <span data-analytics="realtime-label">
        {copy.t("analytics.realtime.activeNow")}
      </span>
    </Badge>
  );
}
